// packages/web/src/components/AdminPromptsView.tsx — misma lógica (listar, versiones, guardar ≠ activar, preview markdown).
// Cambios: lista como tabla, editor en 2 columnas (editor | historial siempre visible), estado
// "cambios sin guardar", pestañas Editar / Vista previa.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, Save, X, ChevronRight } from 'lucide-react';
import { marked } from 'marked';
import { api } from '../api';
import { Alert, Button, Card, INPUT_CLASS, PageHeader, StatusBadge, TABLE } from './ui';

interface PromptSummary {
  engine: string; name: string; active_version_id: number | null; active_version: string | null; active_body_preview: string | null;
  active_updated_at: string | null; active_updated_by: string | null; total_versions: number;
}
interface PromptVersion {
  id: number; engine: string; name: string; version: string; body: string; notes: string | null; is_active: boolean; published_at: string; created_by_email: string | null;
}

const ENGINE_LABEL: Record<string, string> = {
  interview: 'Entrevista · preguntas del profesor virtual',
  assessment: 'Evaluación · extracción de evidencias',
  scoring: 'Puntaje · justificación del reporte',
  enrichment: 'Enriquecimiento · curatorial',
  meta: 'Meta',
};

export function AdminPromptsView({ onClose }: { onClose: () => void }) {
  const [summaries, setSummaries] = useState<PromptSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ engine: string; name: string } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.listPrompts(); setSummaries(r.prompts); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error al cargar prompts'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  if (selected) return <PromptEditor engine={selected.engine} name={selected.name} onBack={() => { setSelected(null); void reload(); }} />;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <PageHeader back={{ label: 'Cerrar', onClick: onClose }} title="Prompts del sistema"
        subtitle="Cada cambio crea una versión nueva. Guardar no activa: la activación se hace desde el historial." />
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando…</p>
      ) : error ? <Alert kind="error">{error}</Alert>
        : summaries.length === 0 ? <Card><p className="text-sm text-slate-600 text-center py-4">No hay prompts registrados aún.</p></Card>
        : (
          <div className={TABLE.wrap}>
            <table className={TABLE.table}>
              <thead className={TABLE.thead}>
                <tr><th className={TABLE.th}>Motor</th><th className={TABLE.th}>Versión activa</th><th className={TABLE.th}>Última activación</th><th className={TABLE.th + ' text-right'}>Versiones</th><th className={TABLE.th}><span className="sr-only">Editar</span></th></tr>
              </thead>
              <tbody>
                {summaries.map((s) => (
                  <tr key={`${s.engine}:${s.name}`} className={TABLE.tr + ' cursor-pointer'} onClick={() => setSelected({ engine: s.engine, name: s.name })}>
                    <td className={TABLE.td + ' max-w-[460px]'}>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setSelected({ engine: s.engine, name: s.name }); }}
                        className="text-left font-semibold text-slate-900 hover:text-marzano-600 rounded focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-500">
                        {ENGINE_LABEL[s.engine] ?? s.engine}
                      </button>
                      <div className="font-mono text-xs text-slate-600">{s.engine} · {s.name}</div>
                      {s.active_body_preview && <div className="text-[13px] text-slate-600 mt-1 line-clamp-1">{s.active_body_preview.replace(/[#*`]/g, '').slice(0, 160)}…</div>}
                    </td>
                    <td className={TABLE.td}>{s.active_version ? <StatusBadge tone="ok">{s.active_version}</StatusBadge> : <StatusBadge tone="warn">Sin activa · usa respaldo</StatusBadge>}</td>
                    <td className={TABLE.td + ' text-slate-700'}>
                      {s.active_updated_at ? new Date(s.active_updated_at).toLocaleDateString('es-CL') : '—'}
                      {s.active_updated_by && <div className="text-xs text-slate-600">{s.active_updated_by}</div>}
                    </td>
                    <td className={TABLE.td + ' text-right tabular-nums'}>{s.total_versions}</td>
                    <td className={TABLE.td + ' text-right'}><ChevronRight aria-hidden className="inline w-4 h-4 text-slate-400" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

function PromptEditor({ engine, name, onBack }: { engine: string; name: string; onBack: () => void }) {
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [notes, setNotes] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await api.listPromptVersions(engine, name);
      setVersions(r.versions);
      const seed = r.versions.find((v) => v.is_active) ?? r.versions[0];
      if (seed) setDraft(seed.body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar el prompt');
    } finally {
      setLoading(false);
    }
  }, [engine, name]);
  useEffect(() => { void reload(); }, [reload]);

  const active = useMemo(() => versions.find((v) => v.is_active) ?? null, [versions]);
  const hasChanges = active ? draft !== active.body : draft.length > 0;

  const save = useCallback(async () => {
    if (draft.length < 50) { setError('El prompt debe tener al menos 50 caracteres.'); return; }
    setSaving(true); setError(null);
    try { await api.createPromptVersion(engine, name, { body: draft, notes: notes.trim() || null }); setNotes(''); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error al guardar'); }
    finally { setSaving(false); }
  }, [draft, notes, engine, name, reload]);

  const activate = useCallback(async (versionId: number) => {
    setActivating(versionId); setError(null);
    try { await api.activatePromptVersion(versionId); await reload(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Error al activar'); }
    finally { setActivating(null); }
  }, [reload]);

  if (loading) return <div className="max-w-6xl mx-auto"><p className="flex items-center gap-2 text-sm text-slate-700"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando…</p></div>;

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      <PageHeader
        back={{ label: 'Prompts del sistema', onClick: onBack }}
        eyebrow={<span className="font-mono">{engine} · {name}</span>}
        title={ENGINE_LABEL[engine] ?? engine}
        actions={active
          ? <span className="text-[13px] text-slate-600">Activa: <StatusBadge tone="ok">{active.version}</StatusBadge> · {new Date(active.published_at).toLocaleDateString('es-CL')}{active.created_by_email && ` · ${active.created_by_email}`}</span>
          : <StatusBadge tone="warn">Sin activación · usa respaldo del código</StatusBadge>}
      />

      {error && (
        <div className="relative">
          <Alert kind="error">{error}</Alert>
          <button type="button" onClick={() => setError(null)} aria-label="Cerrar aviso" className="absolute right-2 top-2 p-1 rounded text-red-700"><X aria-hidden className="w-4 h-4" /></button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
        <section className="rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4">
            <div role="tablist" aria-label="Modo" className="flex gap-1">
              {[{ v: false, l: 'Editar' }, { v: true, l: 'Vista previa' }].map((t) => (
                <button key={t.l} type="button" role="tab" aria-selected={showPreview === t.v} onClick={() => setShowPreview(t.v)}
                  className={`-mb-px px-3 py-3 text-sm border-b-2 ${showPreview === t.v ? 'border-marzano-600 font-semibold text-marzano-700' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>{t.l}</button>
              ))}
            </div>
            <span className={`text-[13px] ${hasChanges ? 'text-amber-800 font-semibold' : 'text-slate-600'}`}>{hasChanges ? '● Cambios sin guardar' : 'Igual a la versión activa'}</span>
          </div>
          <div className="p-4">
            {showPreview ? (
              <div className="prose prose-sm max-w-none text-slate-800 min-h-[480px]" dangerouslySetInnerHTML={{ __html: marked.parse(draft) as string }} />
            ) : (
              <>
                <label htmlFor="prompt-body" className="sr-only">Cuerpo del prompt (Markdown)</label>
                <textarea id="prompt-body" className={INPUT_CLASS + ' !mt-0 font-mono text-[13px] leading-relaxed resize-y min-h-[480px]'} value={draft} onChange={(e) => setDraft(e.target.value)} rows={24} placeholder="Escribe el system prompt en Markdown…" />
                <div className="text-xs text-slate-600 text-right tabular-nums mt-1">{draft.length.toLocaleString('es-CL')} caracteres · mínimo 50</div>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3 border-t border-slate-200 px-4 py-3.5">
            <div className="flex-1 min-w-[240px]">
              <label htmlFor="prompt-notes" className="block text-sm font-medium text-slate-800">Nota del cambio <span className="font-normal text-slate-600">(opcional)</span></label>
              <input id="prompt-notes" className={INPUT_CLASS} type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: ajusté el tono para alumnos de 2° básico" maxLength={500} />
            </div>
            <Button onClick={save} disabled={!hasChanges} loading={saving} leftIcon={<Save aria-hidden className="w-4 h-4" />}>Guardar como nueva versión</Button>
          </div>
          <p className="px-4 pb-3.5 text-[13px] text-slate-600">La versión nueva queda inactiva. Para que el modelo la use, actívala en el historial.</p>
        </section>

        <aside aria-labelledby="hist-t" className="rounded-xl border border-slate-200 bg-white xl:sticky xl:top-20">
          <h2 id="hist-t" className="px-4 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900">Historial · {versions.length} {versions.length === 1 ? 'versión' : 'versiones'}</h2>
          {versions.length === 0 ? <p className="text-sm text-slate-600 text-center py-6">No hay versiones todavía.</p> : (
            <ol className="max-h-[640px] overflow-y-auto divide-y divide-slate-100">
              {versions.map((v) => (
                <li key={v.id} className={`px-4 py-3 space-y-1.5 ${v.is_active ? 'bg-emerald-50/50' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[13px] font-semibold text-slate-900">{v.version}</span>
                    {v.is_active ? <StatusBadge tone="ok">Activa</StatusBadge> : (
                      <Button variant="secondary" size="sm" onClick={() => void activate(v.id)} disabled={activating !== null && activating !== v.id} loading={activating === v.id} leftIcon={<CheckCircle2 aria-hidden className="w-4 h-4" />}>Activar</Button>
                    )}
                  </div>
                  <div className="text-xs text-slate-600">{new Date(v.published_at).toLocaleString('es-CL')}{v.created_by_email && ` · ${v.created_by_email}`}</div>
                  {v.notes && <p className="text-[13px] text-slate-700">{v.notes}</p>}
                  <button type="button" onClick={() => setDraft(v.body)} className="text-[13px] font-semibold text-marzano-600 hover:underline">Cargar en el editor</button>
                </li>
              ))}
            </ol>
          )}
        </aside>
      </div>
    </div>
  );
}
